import { useState, useEffect } from "react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import { Switch } from "../components/ui/switch";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import ComplianceIDCard from "../components/ComplianceIDCard";
import {
  Settings,
  DollarSign,
  Percent,
  Key,
  Save,
  Banknote,
  Mail,
  Send,
  Calendar,
  MessageCircle,
  CreditCard,
  Globe,
  Server,
  Flag
} from "lucide-react";

const SettingsPage = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState({
    registration_fee: 999,
    monthly_fee: 99,
    payment_api_key: "",
    fund_api_key: "",
    // Email settings
    smtp_host: "",
    smtp_port: 587,
    smtp_username: "",
    smtp_password: "",
    smtp_from_email: "admin@echosensecapital.com",
    smtp_from_name: "EchoSense Capital Club",
    admin_notification_email: "admin@echosensecapital.com",
    smtp_use_tls: true,
    // KYC approval alerts (STARSEED-CAPMARKETS-071)
    kyc_approval_alerts_enabled: true,
    kyc_approval_alert_recipient: "",
    // Paytiko Payment Rails settings
    paytiko_api_key: "",
    paytiko_api_secret: "",
    paytiko_merchant_id: "",
    paytiko_webhook_secret: "",
    paytiko_environment: "sandbox",
    // Monaxa settings (MONAXA-REALDATA-008)
    monaxa_api_url: "",
    monaxa_environment: "demo",
    // STARSEED-CAPMARKETS-175 — the partner holder whose book account refs are
    // validated against. Without it the console attach fails closed.
    monaxa_holder_id: "",
    monaxa_proxy_secret: "",
    monaxa_api_key: "",
    monaxa_ca_rest_token: "",
    anthropic_api_key: "",
    // Partner-enablement recipient (forge-20260609-002)
    partner_enablement_email: "",
    partner_agreement_email: "",
    // EchoSense CRM bridge (STARSEED-CRM-020)
    crm_bridge_base_url: "",
    crm_bridge_service_token: "",
    // SumSub KYC settings
    sumsub_app_token: "",
    sumsub_secret_key: "",
    sumsub_webhook_secret: "",
    sumsub_level_name: "id-and-liveness",
    sumsub_environment: "production",
    // Engagement settings
    calendly_url: "",
    intro_video_url: "",
    tawk_property_id: ""
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [flagSaving, setFlagSaving] = useState(null);

  // Toggle a DB-backed feature flag (nav-ia-008). State is set from the
  // server's returned effective flags (audit-logged server-side).
  const toggleFeatureFlag = async (flag, enabled) => {
    setFlagSaving(flag);
    try {
      const res = await api.put("/settings/feature-flags", { flag, enabled });
      setSettings((prev) => ({
        ...prev,
        feature_flags: res.data?.feature_flags || { ...(prev.feature_flags || {}), [flag]: enabled },
      }));
      toast.success(`${flag} ${enabled ? "enabled" : "disabled"} for all users`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update feature flag");
    } finally {
      setFlagSaving(null);
    }
  };
  const [testingEmail, setTestingEmail] = useState(false);
  // Optional override for the test-email recipient — lets admins verify
  // deliverability to external mailboxes (gmail/proton/etc.) without
  // changing the configured `admin_notification_email`. Empty string ⇒
  // backend falls back to admin_notification_email (legacy behavior).
  const [testEmailRecipient, setTestEmailRecipient] = useState("");
  // Monaxa "Test Connection" probe state (forge-20260530-005). `testingMonaxa`
  // drives the button's spinner; `monaxaHealth` holds the last verdict
  // ({ ok, reachable, error, message, elapsed_ms }) for the inline badge.
  const [testingMonaxa, setTestingMonaxa] = useState(false);
  const [monaxaHealth, setMonaxaHealth] = useState(null);
  // Tracks which Paytiko secret fields the admin has touched in this
  // session. If a field has NOT been touched, its value is the masked
  // display (e.g. `pk_live_…wxyz`) returned by GET /settings, and we MUST
  // NOT echo that back to the server on save — doing so would overwrite
  // the real stored secret with the mask. See ticket paytiko-golive-004.
  const [paytikoDirty, setPaytikoDirty] = useState({
    paytiko_api_key:        false,
    paytiko_api_secret:     false,
    paytiko_merchant_id:    false,
    paytiko_webhook_secret: false,
  });
  // Monaxa (MONAXA-REALDATA-008): same dirty-tracking pattern as Paytiko.
  // Two Monaxa-side masked secrets today: `monaxa_proxy_secret` (the
  // egress-proxy shared header) and `monaxa_api_key` (the Bearer token
  // for Monaxa REST API auth). The API URL is plaintext (lands in
  // `system_settings.monaxa_api_url`, shown verbatim in error messages
  // from `MonaxaHttpClient.from_settings`) and `monaxa_environment` is
  // a "demo"/"production" toggle, not a secret.
  const [monaxaDirty, setMonaxaDirty] = useState({
    monaxa_proxy_secret: false,
    monaxa_api_key:      false,
    monaxa_ca_rest_token: false,
    anthropic_api_key: false,
  });
  // EchoSense CRM bridge (STARSEED-CRM-020): same dirty-tracking pattern.
  // Only `crm_bridge_service_token` is a masked secret; the base URL is
  // plaintext (it round-trips in cleartext like `monaxa_api_url` and
  // appears verbatim in `CrmBridgeNotConfigured` error messages).
  const [crmBridgeDirty, setCrmBridgeDirty] = useState({
    crm_bridge_service_token: false,
  });
  // Stripe (localdev-secrets-001): same dirty-tracking pattern. Two
  // masked secrets today: `stripe_webhook_secret` (whsec_...) and
  // `stripe_api_key` (sk_live_... or sk_test_...). Stored in DB so they
  // can be rotated via this UI without redeploys — runtime-changeable
  // config belongs in the database (RTC-R-02 pattern).
  const [stripeDirty, setStripeDirty] = useState({
    stripe_webhook_secret: false,
    stripe_api_key:        false,
  });
  // Resend dirty-tracking — only `resend_api_key` is masked; the
  // from-email is plaintext and doesn't need echo defense.
  const [resendDirty, setResendDirty] = useState({
    resend_api_key: false,
  });
  // SumSub KYC dirty-tracking — three masked secrets (app token, secret
  // key, webhook secret). `sumsub_level_name` / `sumsub_environment` are
  // plaintext config. `testingSumsub` / `sumsubHealth` drive the inline
  // "Test SumSub Connection" probe verdict.
  const [sumsubDirty, setSumsubDirty] = useState({
    sumsub_app_token: false,
    sumsub_secret_key: false,
    sumsub_webhook_secret: false,
  });
  const [testingSumsub, setTestingSumsub] = useState(false);
  const [sumsubHealth, setSumsubHealth] = useState(null);
  // Live status surfaced from GET /paytiko/status — drives the
  // configured-state badge near the Paytiko section header.
  const [paytikoStatus, setPaytikoStatus] = useState({
    configured:  false,
    environment: "sandbox",
    base_url:    "",
  });
  // Show/hide the three direct-API Paytiko credential fields
  // (api_key, api_secret, merchant_id). These are unused under the
  // mediated handoff flow — see ticket forge-20260523-001 and
  // `docs/FINBPM_MONAXA_INTEGRATION_BRIEF.md`. The fields are preserved
  // on the SystemSettings model and round-trip through GET/PUT unchanged;
  // hiding them by default just stops them from misleading admins about
  // what the current integration uses.
  //
  // Session-local on purpose: this is an awareness toggle, not a
  // preference. We want the next admin who lands here to see the
  // banner-first state.
  const [showLegacyPaytikoFields, setShowLegacyPaytikoFields] = useState(false);

  // Detect a masked secret value. Both server masking forms include the
  // unicode ellipsis U+2026:
  //   - long secrets: `pk_live…wxyz`
  //   - short secrets: `***…***`
  // The short-mask form is exercised by the backend test
  // `test_dummy_short_credential_masks_to_stars` in
  // `backend/tests/test_paytiko_settings_routes.py` — if either side
  // changes the mask pattern, that test pins the contract so the two
  // implementations stay in sync. Treat any value containing `…` as
  // "not editable raw" — admin must explicitly touch the field to
  // re-enter the credential.
  const isMaskedSecret = (value) => typeof value === "string" && value.includes("…");

  // Touch a Paytiko field — marks it dirty so it WILL be sent to the
  // server on save.
  const onPaytikoSecretChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setPaytikoDirty(prev => ({ ...prev, [field]: true }));
  };

  // Touch a Monaxa masked-secret field (MONAXA-REALDATA-008). Mirrors
  // `onPaytikoSecretChange` so the save path can distinguish "admin
  // re-typed the secret" from "admin left the masked display value
  // untouched" and skip echoing the masked string back to the server.
  const onMonaxaSecretChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setMonaxaDirty(prev => ({ ...prev, [field]: true }));
  };

  // Touch the CRM bridge service token (STARSEED-CRM-020). Mirrors
  // `onMonaxaSecretChange` so the save path can distinguish "admin
  // re-typed the token" from "admin left the masked display untouched"
  // and skip echoing the masked string back to the server.
  const onCrmBridgeSecretChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setCrmBridgeDirty(prev => ({ ...prev, [field]: true }));
  };

  useEffect(() => {
    fetchSettings();
    fetchPaytikoStatus();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get("/settings");
      setSettings(prev => ({ ...prev, ...response.data }));
      // Reset dirty flags on fresh fetch — the values now reflect the
      // server's masked state.
      setPaytikoDirty({
        paytiko_api_key:        false,
        paytiko_api_secret:     false,
        paytiko_merchant_id:    false,
        paytiko_webhook_secret: false,
      });
      setMonaxaDirty({
        monaxa_proxy_secret: false,
        monaxa_api_key:      false,
        monaxa_ca_rest_token: false,
        anthropic_api_key: false,
      });
      setCrmBridgeDirty({
        crm_bridge_service_token: false,
      });
    } catch (error) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const fetchPaytikoStatus = async () => {
    try {
      const response = await api.get("/paytiko/status");
      setPaytikoStatus({
        configured:  Boolean(response.data?.configured),
        environment: response.data?.environment || "sandbox",
        base_url:    response.data?.base_url || "",
      });
    } catch (error) {
      // Non-fatal: if /paytiko/status fails, leave the default not-configured
      // badge state and keep the Settings page usable (no toast). Log a debug
      // trace so an unexpected failure is still discoverable instead of fully
      // silent.
      // Log only status + message — never the full Axios error, whose `config`
      // carries the Authorization: Bearer header (token leak into console/telemetry).
      console.debug(
        "paytiko status check failed (non-fatal):",
        error?.response?.status,
        error?.message,
      );
    }
  };

  const testSumsubConnection = async () => {
    setTestingSumsub(true);
    setSumsubHealth(null);
    try {
      const r = await api.get("/onboarding/sumsub/health");
      setSumsubHealth(r.data);
    } catch (e) {
      setSumsubHealth({ auth_ok: false, detail: e.response?.data?.detail || "Request failed" });
    } finally {
      setTestingSumsub(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const params = new URLSearchParams();
      params.append("registration_fee", settings.registration_fee);
      params.append("monthly_fee", settings.monthly_fee);

      if (settings.payment_api_key) params.append("payment_api_key", settings.payment_api_key);
      if (settings.fund_api_key) params.append("fund_api_key", settings.fund_api_key);
      
      // Email settings
      if (settings.smtp_host) params.append("smtp_host", settings.smtp_host);
      if (settings.smtp_port) params.append("smtp_port", settings.smtp_port);
      if (settings.smtp_username) params.append("smtp_username", settings.smtp_username);
      if (settings.smtp_password) params.append("smtp_password", settings.smtp_password);
      if (settings.smtp_from_email) params.append("smtp_from_email", settings.smtp_from_email);
      if (settings.smtp_from_name) params.append("smtp_from_name", settings.smtp_from_name);
      if (settings.admin_notification_email) params.append("admin_notification_email", settings.admin_notification_email);
      params.append("smtp_use_tls", settings.smtp_use_tls);
      // KYC approval alerts — always send both: the boolean is a real
      // toggle, and an EMPTY recipient is a legitimate "clear the
      // override, fall back to admin_notification_email" signal.
      params.append("kyc_approval_alerts_enabled", settings.kyc_approval_alerts_enabled);
      params.append("kyc_approval_alert_recipient", settings.kyc_approval_alert_recipient ?? "");
      
      // Paytiko Payment Rails settings — only send fields the admin has
      // touched in this session. Untouched secret fields hold the masked
      // display value (e.g. `pk_live_…wxyz`); echoing that back would
      // overwrite the real stored secret. The backend also defends
      // against masked-value echo-back with a 400, but the frontend
      // skips them first.
      //
      // Empty-string is a legitimate signal (clear the field) — that's
      // why we check `paytikoDirty[field]` instead of truthiness.
      const paytikoSecretFields = [
        "paytiko_api_key",
        "paytiko_api_secret",
        "paytiko_merchant_id",
        "paytiko_webhook_secret",
      ];
      for (const field of paytikoSecretFields) {
        if (paytikoDirty[field] && !isMaskedSecret(settings[field])) {
          params.append(field, settings[field] ?? "");
        }
      }
      if (settings.paytiko_environment) params.append("paytiko_environment", settings.paytiko_environment);

      // Monaxa settings (MONAXA-REALDATA-008). `monaxa_api_url` and
      // `monaxa_environment` are non-secret and round-trip in cleartext.
      // `monaxa_proxy_secret` follows the Paytiko-secret pattern:
      // server returns it masked (contains the ellipsis tell), and we
      // only echo it back if the admin actually re-typed it in this
      // session — otherwise the masked display value would overwrite
      // the real stored secret.
      if (settings.monaxa_api_url !== undefined) {
        params.append("monaxa_api_url", settings.monaxa_api_url ?? "");
      }
      if (settings.monaxa_environment) {
        params.append("monaxa_environment", settings.monaxa_environment);
      }
      // Non-secret, and sent even when empty so an operator can CLEAR it —
      // which is how validation is disabled deliberately rather than by
      // editing the database.
      if (settings.monaxa_holder_id !== undefined) {
        params.append("monaxa_holder_id", settings.monaxa_holder_id ?? "");
      }
      // Partner-enablement recipient (forge-20260609-002): non-secret email,
      // round-trips in cleartext like monaxa_api_url.
      if (settings.partner_enablement_email !== undefined) {
        params.append("partner_enablement_email", settings.partner_enablement_email ?? "");
      }
      if (settings.partner_agreement_email !== undefined) {
        params.append("partner_agreement_email", settings.partner_agreement_email ?? "");
      }
      // FinBPM funding-instruction values (funding-instructions-002/-003):
      // non-secret operational values, round-trip in cleartext.
      if (settings.finbpm_wallet_usdt_trc20 !== undefined) {
        params.append("finbpm_wallet_usdt_trc20", settings.finbpm_wallet_usdt_trc20 ?? "");
      }
      if (settings.finbpm_wallet_usdt_erc20 !== undefined) {
        params.append("finbpm_wallet_usdt_erc20", settings.finbpm_wallet_usdt_erc20 ?? "");
      }
      if (settings.finbpm_wallet_usdc_erc20 !== undefined) {
        params.append("finbpm_wallet_usdc_erc20", settings.finbpm_wallet_usdc_erc20 ?? "");
      }
      if (settings.finbpm_wire_beneficiary !== undefined) {
        params.append("finbpm_wire_beneficiary", settings.finbpm_wire_beneficiary ?? "");
      }
      if (settings.finbpm_wire_bank_name !== undefined) {
        params.append("finbpm_wire_bank_name", settings.finbpm_wire_bank_name ?? "");
      }
      if (settings.finbpm_wire_bank_address !== undefined) {
        params.append("finbpm_wire_bank_address", settings.finbpm_wire_bank_address ?? "");
      }
      if (settings.finbpm_wire_account_number !== undefined) {
        params.append("finbpm_wire_account_number", settings.finbpm_wire_account_number ?? "");
      }
      if (settings.finbpm_wire_swift !== undefined) {
        params.append("finbpm_wire_swift", settings.finbpm_wire_swift ?? "");
      }
      if (settings.finbpm_wire_routing !== undefined) {
        params.append("finbpm_wire_routing", settings.finbpm_wire_routing ?? "");
      }
      if (settings.finbpm_instructions_reply_to !== undefined) {
        params.append("finbpm_instructions_reply_to", settings.finbpm_instructions_reply_to ?? "");
      }
      if (monaxaDirty.monaxa_proxy_secret && !isMaskedSecret(settings.monaxa_proxy_secret)) {
        params.append("monaxa_proxy_secret", settings.monaxa_proxy_secret ?? "");
      }
      if (monaxaDirty.monaxa_api_key && !isMaskedSecret(settings.monaxa_api_key)) {
        params.append("monaxa_api_key", settings.monaxa_api_key ?? "");
      }
      // CAPMARKETS-227 — the client-area (/ca-rest/*) credential. Same
      // masked-echo defence as every secret above: only sent when the admin
      // re-typed it this session AND the value is not the masked display.
      if (monaxaDirty.monaxa_ca_rest_token && !isMaskedSecret(settings.monaxa_ca_rest_token)) {
        params.append("monaxa_ca_rest_token", settings.monaxa_ca_rest_token ?? "");
      }
      // CAPMARKETS-230 — vision key for reading statement screenshots. Same
      // masked-echo defence: only sent when re-typed this session AND not the
      // masked display value.
      if (monaxaDirty.anthropic_api_key && !isMaskedSecret(settings.anthropic_api_key)) {
        params.append("anthropic_api_key", settings.anthropic_api_key ?? "");
      }

      // EchoSense CRM bridge (STARSEED-CRM-020). The base URL is
      // non-secret and round-trips in cleartext like monaxa_api_url;
      // the service token follows the masked-echo defense — only sent
      // when the admin re-typed it this session AND the value isn't the
      // masked display (the backend 400s on masked echo as the second
      // line of defense).
      if (settings.crm_bridge_base_url !== undefined) {
        params.append("crm_bridge_base_url", settings.crm_bridge_base_url ?? "");
      }
      if (crmBridgeDirty.crm_bridge_service_token && !isMaskedSecret(settings.crm_bridge_service_token)) {
        params.append("crm_bridge_service_token", settings.crm_bridge_service_token ?? "");
      }

      // Stripe webhook signing secret. Same masked-echo defense as the
      // Monaxa secrets above — only send when the admin has typed in
      // this session AND the value isn't the masked display.
      if (stripeDirty.stripe_webhook_secret && !isMaskedSecret(settings.stripe_webhook_secret)) {
        params.append("stripe_webhook_secret", settings.stripe_webhook_secret ?? "");
      }
      if (stripeDirty.stripe_api_key && !isMaskedSecret(settings.stripe_api_key)) {
        params.append("stripe_api_key", settings.stripe_api_key ?? "");
      }
      // Resend transactional email
      if (resendDirty.resend_api_key && !isMaskedSecret(settings.resend_api_key)) {
        params.append("resend_api_key", settings.resend_api_key ?? "");
      }
      if (settings.resend_from_email !== undefined && settings.resend_from_email !== null) {
        params.append("resend_from_email", settings.resend_from_email ?? "");
      }

      // SumSub KYC — masked-echo defense on the three secret fields; the
      // level name + environment round-trip in cleartext.
      const sumsubSecretFields = ["sumsub_app_token", "sumsub_secret_key", "sumsub_webhook_secret"];
      for (const field of sumsubSecretFields) {
        if (sumsubDirty[field] && !isMaskedSecret(settings[field])) {
          params.append(field, settings[field] ?? "");
        }
      }
      if (settings.sumsub_level_name) params.append("sumsub_level_name", settings.sumsub_level_name);
      if (settings.sumsub_environment) params.append("sumsub_environment", settings.sumsub_environment);

      // Engagement settings
      if (settings.calendly_url) params.append("calendly_url", settings.calendly_url);
      if (settings.intro_video_url) params.append("intro_video_url", settings.intro_video_url);
      if (settings.tawk_property_id) params.append("tawk_property_id", settings.tawk_property_id);

      const saveRes = await api.put(`/settings?${params.toString()}`);
      if (saveRes.data?.wire_change_pending) {
        // STARSEED-CAPMARKETS-064: wire details are dual-control.
        toast.info(
          "Wire-detail changes are pending — a second admin must approve them "
          + "on the Funding Queue page before members see the new details.",
          { duration: 10000 },
        );
      } else {
        toast.success("Settings saved successfully");
      }
      // Re-fetch on success so masked secrets refresh and the configured
      // badge reflects the new state without a manual page refresh.
      await Promise.all([fetchSettings(), fetchPaytikoStatus()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      // Pass override as query param only when admin typed one in; if the
      // field is empty, the backend falls back to admin_notification_email.
      const trimmed = (testEmailRecipient || "").trim();
      const url = trimmed
        ? `/settings/test-email?to_email=${encodeURIComponent(trimmed)}`
        : "/settings/test-email";
      const response = await api.post(url);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test email");
    } finally {
      setTestingEmail(false);
    }
  };

  // Monaxa "Test Connection" — pings the Monaxa REST API through the full
  // configured chain (incl. the egress proxy hop) via GET /monaxa/health.
  // The endpoint always returns 200 with a structured verdict; we render
  // green/red from `ok`. A thrown error is only the admin-gate 403 or a
  // transport failure reaching our own backend, handled in catch.
  const handleTestMonaxa = async () => {
    setTestingMonaxa(true);
    setMonaxaHealth(null);
    try {
      const response = await api.get("/monaxa/health");
      setMonaxaHealth(response.data);
      if (response.data.ok) {
        toast.success("Monaxa API reachable");
      } else {
        toast.error(response.data.message || "Monaxa not reachable");
      }
    } catch (error) {
      const detail = error.response?.data?.detail || "Connection test failed";
      setMonaxaHealth({ ok: false, reachable: false, error: "request_failed", message: detail });
      toast.error(detail);
    } finally {
      setTestingMonaxa(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Settings" subtitle="Loading...">
        <div className="animate-pulse space-y-4">
          <div className="h-48 bg-surface rounded"></div>
          <div className="h-48 bg-surface rounded"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout 
      title="System Settings"
      subtitle="Configure fees and API integrations"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Compliance ID Card — single-screen trust artifact for banks /
            attorneys / KYC reviewers. Spans full width at the top. Reuses
            this page's settings fetch (masked secrets) instead of its own. */}
        <ComplianceIDCard settings={settings} settingsLoading={loading} />

        {/* Feature Flags (nav-ia-008) — DB-backed global toggles, no redeploy */}
        <Card className="bg-surface border-white/5 lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-white font-serif flex items-center gap-2">
              <Flag className="text-gold" size={20} />
              Feature Flags
            </CardTitle>
            <CardDescription>Toggle in-development UI for all users instantly — no redeploy.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <Label className="text-gray-300" htmlFor="flag-nav-v2">Redesigned sidebar (nav_v2)</Label>
                <p className="text-xs text-gray-500 mt-1">
                  Shows the new role-aware sidebar to all users. Off = the current sidebar.
                  Beta testers can preview ahead via <code>localStorage es-ff-nav-v2 = "1"</code>.
                </p>
              </div>
              <Switch
                checked={!!settings.feature_flags?.nav_v2}
                disabled={flagSaving === "nav_v2"}
                onCheckedChange={(v) => toggleFeatureFlag("nav_v2", v)}
                id="flag-nav-v2"
                data-testid="flag-nav-v2"
              />
            </div>

            {/* STARSEED-CRM-010/012 — dual-run sync + authority flip levers.
                Flipping crm_platform_authority is HUMAN-GATED: only per the
                recorded sign-off + flip checklist on ticket STARSEED-CRM-010. */}
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <Label className="text-gray-300" htmlFor="flag-crm-dual-run-sync">CRM dual-run sync (crm_dual_run_sync)</Label>
                <p className="text-xs text-gray-500 mt-1">
                  Runs the periodic Mongo → EchoSense CRM sync cycle (STARSEED-CRM-010).
                  Off pauses the sync at the next tick — the pre-flip rollback lever.
                </p>
              </div>
              {/* Strict === true on both CRM switches so the display can never
                  claim ON for a non-boolean truthy value that the fail-closed
                  runtime gates (crmAdmin.js / require_platform_authority)
                  would treat as OFF. */}
              <Switch
                checked={settings.feature_flags?.crm_dual_run_sync === true}
                disabled={flagSaving === "crm_dual_run_sync"}
                onCheckedChange={(v) => toggleFeatureFlag("crm_dual_run_sync", v)}
                id="flag-crm-dual-run-sync"
                data-testid="flag-crm-dual-run-sync"
              />
            </div>

            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <Label className="text-gray-300" htmlFor="flag-crm-platform-authority">EchoSense CRM authority (crm_platform_authority)</Label>
                <p className="text-xs text-gray-500 mt-1">
                  ON routes the admin lead pages through the platform CRM (STARSEED-CRM-012);
                  OFF keeps the legacy Mongo data path. Flip ONLY per the human-gated
                  checklist recorded on STARSEED-CRM-010 — this switch is the rollback lever.
                </p>
              </div>
              <Switch
                checked={settings.feature_flags?.crm_platform_authority === true}
                disabled={flagSaving === "crm_platform_authority"}
                onCheckedChange={(v) => toggleFeatureFlag("crm_platform_authority", v)}
                id="flag-crm-platform-authority"
                data-testid="flag-crm-platform-authority"
              />
            </div>

            {/* STARSEED-CAPMARKETS-211 — the shadow ledger's on/off lever.
                RTC-R-02 puts the knob in the DB rather than an env var so an
                operator can stop it without a redeploy; that property is only
                real if the operator can actually reach it, and until this
                switch existed the flag was settable solely by hand-calling
                PUT /settings/feature-flags. The scheduler job registers at
                startup regardless of the flag and re-reads it every tick, so
                flipping here takes effect within one interval — no restart. */}
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <Label className="text-gray-300" htmlFor="flag-partner-ledger-shadow">Partner ledger shadow (partner_ledger_shadow)</Label>
                <p className="text-xs text-gray-500 mt-1">
                  Reconstructs member balances from the partner transaction ledger once
                  per interval and records them beside the figures members are shown
                  (STARSEED-CAPMARKETS-211). Members see NOTHING from this — it writes
                  to a separate shadow store to accumulate the comparison
                  CAPMARKETS-212 needs before any of it goes member-facing. Off stops
                  it at the next tick.
                </p>
              </div>
              <Switch
                checked={settings.feature_flags?.partner_ledger_shadow === true}
                disabled={flagSaving === "partner_ledger_shadow"}
                onCheckedChange={(v) => toggleFeatureFlag("partner_ledger_shadow", v)}
                id="flag-partner-ledger-shadow"
                data-testid="flag-partner-ledger-shadow"
              />
            </div>

            {/* STARSEED-CAPMARKETS-325 — the per-account card on the Partner
                Account page. Admins see it for any member either way (marked
                as a preview); this switch decides whether MEMBERS see it. Off
                by default so the operator reads every member's real figures
                first. Semantic tokens, not the gray-* this section's older
                switches use: those fail AA in the light theme
                (LeadManagementPage.contrast.test.js). */}
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <Label className="text-foreground" htmlFor="flag-partner-account-breakdown">Per-account view for members (partner_account_breakdown)</Label>
                <p className="text-xs text-foreground-muted mt-1">
                  Shows each member their partner accounts one by one: what has moved into
                  the pool, what is in the account now, and the last statement
                  (STARSEED-CAPMARKETS-325). Admins can already see it for any member on the
                  Partner Account page. Turn on once those figures have been checked.
                </p>
              </div>
              <Switch
                checked={settings.feature_flags?.partner_account_breakdown === true}
                disabled={flagSaving === "partner_account_breakdown"}
                onCheckedChange={(v) => toggleFeatureFlag("partner_account_breakdown", v)}
                id="flag-partner-account-breakdown"
                data-testid="flag-partner-account-breakdown"
              />
            </div>

            {/* STARSEED-CAPMARKETS-356 — the hourly watch always notices new
                partner accounts and emails about them; this switch decides
                whether it may also ATTACH one whose label names exactly one
                member. Off by default. Only accounts that appear while it is on
                are ever attached. */}
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <Label className="text-foreground" htmlFor="flag-partner-account-auto-attach">Attach new partner accounts automatically (partner_account_auto_attach)</Label>
                <p className="text-xs text-foreground-muted mt-1">
                  When the partner opens an account whose label names exactly one member, by a
                  nickname already on their accounts or by their name, attach it to them and
                  send an email (STARSEED-CAPMARKETS-356). Anything less certain waits for a
                  person. Accounts that appeared before this was turned on are never attached.
                  Undo an attach with Detach on the Partner Accounts page.
                </p>
              </div>
              <Switch
                checked={settings.feature_flags?.partner_account_auto_attach === true}
                disabled={flagSaving === "partner_account_auto_attach"}
                onCheckedChange={(v) => toggleFeatureFlag("partner_account_auto_attach", v)}
                id="flag-partner-account-auto-attach"
                data-testid="flag-partner-account-auto-attach"
              />
            </div>

            {/* STARSEED-CRM-020 — workload-side CRM bridge config. Lives
                beside the CRM levers above so the operator configures the
                bridge in the same place they flip it on. Unlike the
                toggles (which save instantly), these inputs save via the
                page's Save Settings button, like every other credential
                field on this page. */}
            <div className="space-y-4 border-t border-white/10 pt-4">
              <div>
                <p className="text-sm font-semibold text-gray-200">EchoSense CRM bridge</p>
                <p className="text-xs text-gray-500 mt-1">
                  Platform bridge endpoint + service token used by the CRM lead pages and the
                  dual-run sync (STARSEED-CRM-008/020). Set or rotate here — no database access
                  needed; the bridge reads settings fresh on each call, so a rotation takes effect
                  on the next request. Saved with the <strong>Save Settings</strong> button at the
                  bottom of the page.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Bridge base URL</Label>
                  <Input
                    type="text"
                    value={settings.crm_bridge_base_url || ""}
                    onChange={(e) => setSettings(prev => ({ ...prev, crm_bridge_base_url: e.target.value }))}
                    className="bg-white border-slate-200 text-slate-900"
                    placeholder="https://api.tryant.ai"
                    data-testid="crm-bridge-base-url-input"
                  />
                  <p className="text-gray-600 text-xs">
                    Non-secret; bare origin of the platform bridge API. Empty means the bridge is
                    unconfigured — bridge-backed pages fail with a typed error naming this field.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Bridge service token</Label>
                  <Input
                    type={isMaskedSecret(settings.crm_bridge_service_token) ? "text" : "password"}
                    value={settings.crm_bridge_service_token || ""}
                    onChange={(e) => onCrmBridgeSecretChange("crm_bridge_service_token", e.target.value)}
                    className="bg-white border-slate-200 text-slate-900"
                    placeholder="service token issued by the platform operator"
                    data-testid="crm-bridge-service-token-input"
                  />
                  {isMaskedSecret(settings.crm_bridge_service_token) && (
                    <p className="text-gray-500 text-xs">
                      Token on file. Re-type to rotate; leave masked to keep the current value.
                    </p>
                  )}
                  <p className="text-gray-600 text-xs">
                    Masked after save — the raw value never round-trips back to the browser.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Fee Settings */}
        <Card className="bg-surface border-white/5">
          <CardHeader>
            <CardTitle className="text-white font-serif flex items-center gap-2">
              <DollarSign className="text-gold" size={20} />
              Fee Settings
            </CardTitle>
            <CardDescription>Configure membership fees</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Registration Fee ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={settings.registration_fee}
                onChange={(e) => setSettings(prev => ({ ...prev, registration_fee: parseFloat(e.target.value) }))}
                className="bg-white border-slate-200 text-slate-900"
                data-testid="registration-fee-input"
              />
              <p className="text-gray-600 text-xs">One-time fee for new members</p>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Monthly Fee ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={settings.monthly_fee}
                onChange={(e) => setSettings(prev => ({ ...prev, monthly_fee: parseFloat(e.target.value) }))}
                className="bg-white border-slate-200 text-slate-900"
                data-testid="monthly-fee-input"
              />
              <p className="text-gray-600 text-xs">Recurring monthly membership fee</p>
            </div>
          </CardContent>
        </Card>

        {/* Comp Fund + Multi-Tier Commission config removed
            (STARSEED-CAPMARKETS-080) — compensation surfaces retired per
            attorney direction. */}

        {/* API Keys */}
        <Card className="bg-surface border-white/5 lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-white font-serif flex items-center gap-2">
              <Key className="text-gold" size={20} />
              API Integration Keys
            </CardTitle>
            <CardDescription>Configure payment processor and fund tracking APIs</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Other APIs */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Payment Processor API Key</Label>
                <Input
                  type="password"
                  value={settings.payment_api_key || ""}
                  onChange={(e) => setSettings(prev => ({ ...prev, payment_api_key: e.target.value }))}
                  className="bg-white border-slate-200 text-slate-900"
                  placeholder="Enter API key when available"
                  data-testid="payment-api-input"
                />
                <p className="text-gray-600 text-xs">For processing registration and membership payments</p>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">Fund Tracking API Key</Label>
                <Input
                  type="password"
                  value={settings.fund_api_key || ""}
                  onChange={(e) => setSettings(prev => ({ ...prev, fund_api_key: e.target.value }))}
                  className="bg-white border-slate-200 text-slate-900"
                  placeholder="Enter API key when available"
                  data-testid="fund-api-input"
                />
                <p className="text-gray-600 text-xs">For automatic fund performance tracking from external data provider</p>
              </div>
            </div>

            <div className="p-4 rounded bg-gold/10 border border-gold/30">
              <p className="text-gold text-sm font-medium">Note</p>
              <p className="text-gray-400 text-xs mt-1">
                API keys can be added later. The system currently supports manual processing via wire transfer.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Stripe Live Credentials — localdev-secrets-001.
            Stored in DB rather than env so admins can rotate without
            redeploys. All Stripe call sites resolve via
            `services.settings_service.get_stripe_api_key()` (DB-then-env);
            the `/webhook/stripe` handler resolves the signing secret via
            inline DB-then-env lookup. Env vars remain as fallback. */}
        <Card className="bg-surface border-white/5 lg:col-span-2" data-testid="settings-stripe-card">
          <CardHeader>
            <CardTitle className="text-white font-serif flex items-center gap-2">
              <Key className="text-gold" size={20} />
              Stripe Live Credentials
            </CardTitle>
            <CardDescription>
              Secret API Key (creates Checkout sessions, retrieves payments) and Webhook Signing Secret (HMAC-verifies inbound webhook events). Both rotatable here without redeploy.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Secret API Key</Label>
              <Input
                type={isMaskedSecret(settings.stripe_api_key) ? "text" : "password"}
                value={settings.stripe_api_key || ""}
                onChange={(e) => {
                  setSettings(prev => ({ ...prev, stripe_api_key: e.target.value }));
                  setStripeDirty(prev => ({ ...prev, stripe_api_key: true }));
                }}
                className="bg-white border-slate-200 text-slate-900 font-mono"
                placeholder="sk_live_... (or sk_test_... in non-production)"
                data-testid="stripe-api-key-input"
              />
              {isMaskedSecret(settings.stripe_api_key) && (
                <p className="text-gray-500 text-xs">
                  Key on file. Re-type to update; leave masked to keep current value.
                </p>
              )}
              <p className="text-gray-600 text-xs">
                Get this from <strong>Stripe Dashboard → Developers → API keys → Secret key → Reveal</strong>. Starts with <code>sk_live_</code> (live mode) or <code>sk_test_</code> (test mode). Used to create Checkout sessions, retrieve payments, manage subscriptions, and look up customers. Persisted in the DB — rotation does not require a redeploy.
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Webhook Signing Secret</Label>
              <Input
                type={isMaskedSecret(settings.stripe_webhook_secret) ? "text" : "password"}
                value={settings.stripe_webhook_secret || ""}
                onChange={(e) => {
                  setSettings(prev => ({ ...prev, stripe_webhook_secret: e.target.value }));
                  setStripeDirty(prev => ({ ...prev, stripe_webhook_secret: true }));
                }}
                className="bg-white border-slate-200 text-slate-900 font-mono"
                placeholder="whsec_..."
                data-testid="stripe-webhook-secret-input"
              />
              {isMaskedSecret(settings.stripe_webhook_secret) && (
                <p className="text-gray-500 text-xs">
                  Secret on file. Re-type to update; leave masked to keep current value.
                </p>
              )}
              <p className="text-gray-600 text-xs">
                Get this from <strong>Stripe Dashboard → Developers → Webhooks → click your endpoint → Reveal signing secret</strong>. Starts with <code>whsec_</code>. Persisted in the DB (not env), so rotation does not require a redeploy.
              </p>
            </div>
            <div className="p-3 rounded bg-emerald-500/10 border border-emerald-500/30 text-xs text-gray-300">
              <strong className="text-emerald-400 block mb-1">Why this lives in the DB, not env</strong>
              Runtime-rotatable secrets belong in the database, not in build/boot-time env vars. Storing this here keeps the secret out of git, masks it in the UI, and lets you rotate without a redeploy. The webhook handler in <code>routes/payments.py</code> reads from this field first and falls back to the <code>STRIPE_WEBHOOK_SECRET</code> env var for backward compatibility.
            </div>
          </CardContent>
        </Card>

        {/* Resend transactional email — preferred path when set, falls
            back to SMTP. Better deliverability than M365/GoDaddy SMTP,
            no MFA entanglement, audit dashboard at resend.com. */}
        <Card className="bg-surface border-white/5 lg:col-span-2" data-testid="settings-resend-card">
          <CardHeader>
            <CardTitle className="text-white font-serif flex items-center gap-2">
              <Mail className="text-gold" size={20} />
              Resend (Transactional Email)
            </CardTitle>
            <CardDescription>
              When configured, the platform sends all transactional email (welcome, password reset, redemption alerts) via Resend's HTTP API instead of SMTP — better deliverability, no MFA/app-password drama. Falls back to SMTP below if Resend rejects a send.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Resend API Key</Label>
              <Input
                type={isMaskedSecret(settings.resend_api_key) ? "text" : "password"}
                value={settings.resend_api_key || ""}
                onChange={(e) => {
                  setSettings(prev => ({ ...prev, resend_api_key: e.target.value }));
                  setResendDirty(prev => ({ ...prev, resend_api_key: true }));
                }}
                className="bg-white border-slate-200 text-slate-900 font-mono"
                placeholder="re_..."
                data-testid="resend-api-key-input"
              />
              {isMaskedSecret(settings.resend_api_key) && (
                <p className="text-gray-500 text-xs">
                  Key on file. Re-type to update; leave masked to keep current value.
                </p>
              )}
              <p className="text-gray-600 text-xs">
                Get this from <strong>resend.com/api-keys → Create API Key → Sending access, scoped to echosensecapital.com</strong>. Starts with <code>re_</code>.
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Send-as Email Address</Label>
              <Input
                type="email"
                value={settings.resend_from_email || ""}
                onChange={(e) => setSettings(prev => ({ ...prev, resend_from_email: e.target.value }))}
                className="bg-white border-slate-200 text-slate-900"
                placeholder="noreply@notifications.echosensecapital.com"
                data-testid="resend-from-email-input"
              />
              <p className="text-gray-600 text-xs">
                Must be on a domain verified in your Resend dashboard. Use an <strong>apex</strong> address — <code>noreply@echosensecapital.com</code> — because DMARC on this domain is <code>p=reject</code> with strict alignment (<code>adkim=s</code>), so a subdomain From fails and the mail is rejected outright, not just spam-foldered. Leaving this blank does <strong>not</strong> fall back to the apex: it falls back to <code>noreply@notifications.echosensecapital.com</code>, a subdomain with no SPF or DKIM published. Set this explicitly.
              </p>
            </div>
            <div className="p-3 rounded bg-emerald-500/10 border border-emerald-500/30 text-xs text-gray-300">
              <strong className="text-emerald-400 block mb-1">Why Resend?</strong>
              Bypasses GoDaddy/M365 SMTP AUTH limitations (no app passwords, no MFA toggles, no &quot;Modern authentication&quot; admin gymnastics). Free tier: 3,000 emails/month, 100/day. Includes auto SPF/DKIM/DMARC, delivery dashboard, and bounce webhooks.
            </div>
          </CardContent>
        </Card>

        {/* SumSub KYC/AML — live identity verification. Stored in DB so the
            app token / secret key / webhook secret rotate without redeploy.
            Resolved at request time via settings_service.get_sumsub_config(). */}
        <Card className="bg-surface border-white/5 lg:col-span-2" data-testid="settings-sumsub-card">
          <CardHeader>
            <CardTitle className="text-white font-serif flex items-center gap-2">
              <Key className="text-gold" size={20} />
              SumSub KYC / Identity Verification
            </CardTitle>
            <CardDescription>
              Live identity &amp; liveness verification for members. Get these from your SumSub Dashboard → Dev Space. The webhook target in SumSub must point to <code>/api/kyc/sumsub/webhook</code> (SHA256).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-300">App Token</Label>
              <Input
                type={isMaskedSecret(settings.sumsub_app_token) ? "text" : "password"}
                value={settings.sumsub_app_token || ""}
                onChange={(e) => {
                  setSettings(prev => ({ ...prev, sumsub_app_token: e.target.value }));
                  setSumsubDirty(prev => ({ ...prev, sumsub_app_token: true }));
                }}
                className="bg-white border-slate-200 text-slate-900 font-mono"
                placeholder="prd:... (or sbx:... for sandbox)"
                data-testid="sumsub-app-token-input"
              />
              {isMaskedSecret(settings.sumsub_app_token) && (
                <p className="text-gray-500 text-xs">Token on file. Re-type to update; leave masked to keep current value.</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Secret Key</Label>
              <Input
                type={isMaskedSecret(settings.sumsub_secret_key) ? "text" : "password"}
                value={settings.sumsub_secret_key || ""}
                onChange={(e) => {
                  setSettings(prev => ({ ...prev, sumsub_secret_key: e.target.value }));
                  setSumsubDirty(prev => ({ ...prev, sumsub_secret_key: true }));
                }}
                className="bg-white border-slate-200 text-slate-900 font-mono"
                placeholder="Secret key paired with the App Token"
                data-testid="sumsub-secret-key-input"
              />
              {isMaskedSecret(settings.sumsub_secret_key) && (
                <p className="text-gray-500 text-xs">Secret on file. Re-type to update; leave masked to keep current value.</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Webhook Secret</Label>
              <Input
                type={isMaskedSecret(settings.sumsub_webhook_secret) ? "text" : "password"}
                value={settings.sumsub_webhook_secret || ""}
                onChange={(e) => {
                  setSettings(prev => ({ ...prev, sumsub_webhook_secret: e.target.value }));
                  setSumsubDirty(prev => ({ ...prev, sumsub_webhook_secret: true }));
                }}
                className="bg-white border-slate-200 text-slate-900 font-mono"
                placeholder="Secret set in SumSub Webhook Manager"
                data-testid="sumsub-webhook-secret-input"
              />
              {isMaskedSecret(settings.sumsub_webhook_secret) && (
                <p className="text-gray-500 text-xs">Secret on file. Re-type to update; leave masked to keep current value.</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Verification Level Name</Label>
                <Input
                  type="text"
                  value={settings.sumsub_level_name || ""}
                  onChange={(e) => setSettings(prev => ({ ...prev, sumsub_level_name: e.target.value }))}
                  className="bg-white border-slate-200 text-slate-900 font-mono"
                  placeholder="id-and-liveness"
                  data-testid="sumsub-level-name-input"
                />
                <p className="text-gray-600 text-xs">From SumSub → Integrations → Verification Levels.</p>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Environment</Label>
                <select
                  value={settings.sumsub_environment || "production"}
                  onChange={(e) => setSettings(prev => ({ ...prev, sumsub_environment: e.target.value }))}
                  className="w-full h-10 rounded-md bg-white border border-slate-200 text-slate-900 px-3 text-sm"
                  data-testid="sumsub-environment-select"
                >
                  <option value="production">production</option>
                  <option value="sandbox">sandbox</option>
                </select>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Button
                type="button"
                onClick={testSumsubConnection}
                disabled={testingSumsub}
                className="bg-gold/90 hover:bg-gold text-navy font-semibold"
                data-testid="sumsub-test-connection-btn"
              >
                {testingSumsub ? "Testing…" : "Test SumSub Connection"}
              </Button>
              {sumsubHealth && (
                <span
                  className={`text-xs px-2 py-1 rounded border ${
                    sumsubHealth.auth_ok
                      ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
                      : "bg-red-500/10 border-red-500/30 text-red-300"
                  }`}
                  data-testid="sumsub-health-badge"
                >
                  {sumsubHealth.auth_ok ? "✓ Credentials accepted" : "✗ "}{!sumsubHealth.auth_ok && (sumsubHealth.detail || "Not connected")}
                  {sumsubHealth.auth_ok && sumsubHealth.webhook_secret_set === false && " · webhook secret missing"}
                </span>
              )}
            </div>
            <div className="p-3 rounded bg-emerald-500/10 border border-emerald-500/30 text-xs text-gray-300">
              <strong className="text-emerald-400 block mb-1">Save first, then Test</strong>
              Enter the values, click <strong>Save Settings</strong> at the bottom, then click <strong>Test SumSub Connection</strong> — a green badge means SumSub accepts your credentials. The webhook URL to enter in SumSub is <code>https://api.echosensecapital.com/api/kyc/sumsub/webhook</code>.
            </div>
          </CardContent>
        </Card>

        {/* Email Notification Settings (SMTP fallback) */}
        <Card className="bg-surface border-white/5 lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-white font-serif flex items-center gap-2">
              <Mail className="text-gold" size={20} />
              Email Notification Settings
            </CardTitle>
            <CardDescription>Configure SMTP for redemption status notifications to admin@echosensecapital.com</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* SMTP Server Settings */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">SMTP Host</Label>
                <Input
                  type="text"
                  value={settings.smtp_host || ""}
                  onChange={(e) => setSettings(prev => ({ ...prev, smtp_host: e.target.value }))}
                  className="bg-white border-slate-200 text-slate-900"
                  placeholder="smtp.office365.com"
                  data-testid="smtp-host-input"
                />
                <p className="text-gray-600 text-xs">Microsoft 365: smtp.office365.com</p>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">SMTP Port</Label>
                <Input
                  type="number"
                  value={settings.smtp_port || 587}
                  onChange={(e) => setSettings(prev => ({ ...prev, smtp_port: parseInt(e.target.value) }))}
                  className="bg-white border-slate-200 text-slate-900"
                  placeholder="587"
                  data-testid="smtp-port-input"
                />
                <p className="text-gray-600 text-xs">Usually 587 (TLS) or 465 (SSL)</p>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">Use TLS</Label>
                <Select 
                  value={settings.smtp_use_tls ? "true" : "false"} 
                  onValueChange={(v) => setSettings(prev => ({ ...prev, smtp_use_tls: v === "true" }))}
                >
                  <SelectTrigger className="bg-surface border-platinum/20 text-platinum">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-surface border-white/10">
                    <SelectItem value="true">Yes (Recommended)</SelectItem>
                    <SelectItem value="false">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* SMTP Credentials */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">SMTP Username</Label>
                <Input
                  type="text"
                  value={settings.smtp_username || ""}
                  onChange={(e) => setSettings(prev => ({ ...prev, smtp_username: e.target.value }))}
                  className="bg-white border-slate-200 text-slate-900"
                  placeholder="admin@echosensecapital.com"
                  data-testid="smtp-username-input"
                />
                <p className="text-gray-600 text-xs">Usually your full email address</p>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">SMTP Password</Label>
                <Input
                  type="password"
                  value={settings.smtp_password || ""}
                  onChange={(e) => setSettings(prev => ({ ...prev, smtp_password: e.target.value }))}
                  className="bg-white border-slate-200 text-slate-900"
                  placeholder="Enter email password"
                  data-testid="smtp-password-input"
                />
                <p className="text-gray-600 text-xs">M365 app password (not your login password) — see fallback setup below</p>
              </div>
            </div>

            {/* From/To Settings */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">From Email</Label>
                <Input
                  type="email"
                  value={settings.smtp_from_email || "admin@echosensecapital.com"}
                  onChange={(e) => setSettings(prev => ({ ...prev, smtp_from_email: e.target.value }))}
                  className="bg-white border-slate-200 text-slate-900"
                  placeholder="admin@echosensecapital.com"
                  data-testid="smtp-from-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">From Name</Label>
                <Input
                  type="text"
                  value={settings.smtp_from_name || "EchoSense Capital Club"}
                  onChange={(e) => setSettings(prev => ({ ...prev, smtp_from_name: e.target.value }))}
                  className="bg-white border-slate-200 text-slate-900"
                  placeholder="EchoSense Capital Club"
                  data-testid="smtp-from-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">Admin Notification Email</Label>
                <Input
                  type="email"
                  value={settings.admin_notification_email || "admin@echosensecapital.com"}
                  onChange={(e) => setSettings(prev => ({ ...prev, admin_notification_email: e.target.value }))}
                  className="bg-white border-slate-200 text-slate-900"
                  placeholder="admin@echosensecapital.com"
                  data-testid="admin-email-input"
                />
              </div>
            </div>

            {/* KYC approval alerts (STARSEED-CAPMARKETS-071) — the 5-minute
                sweep that emails admins when a member reaches KYC Pending /
                Accreditation Pending. Non-secret; round-trips in cleartext. */}
            <div className="p-4 rounded bg-surface-highlight border border-white/10 space-y-3">
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-white font-medium">KYC Approval Alerts</p>
                  <p className="text-gray-400 text-sm">
                    Email admins when a member's onboarding is waiting on an admin approval
                    (KYC / accreditation review). One digest per 5-minute sweep, only when
                    something is new.
                  </p>
                </div>
                <Switch
                  checked={settings.kyc_approval_alerts_enabled !== false}
                  onCheckedChange={(v) => setSettings(prev => ({ ...prev, kyc_approval_alerts_enabled: v }))}
                  data-testid="kyc-approval-alerts-enabled-switch"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300 text-xs">Alert recipient (optional)</Label>
                <Input
                  type="email"
                  value={settings.kyc_approval_alert_recipient || ""}
                  onChange={(e) => setSettings(prev => ({ ...prev, kyc_approval_alert_recipient: e.target.value }))}
                  className="bg-white border-slate-200 text-slate-900"
                  placeholder={settings.admin_notification_email || "admin@eqhosensecapital.com"}
                  data-testid="kyc-approval-alert-recipient-input"
                />
                <p className="text-gray-500 text-xs">
                  Leave blank to send to the Admin Notification Email above (e.g. set a dedicated
                  KYC-desk inbox here without changing other admin notifications).
                </p>
              </div>
            </div>

            {/* Test Email — optional arbitrary recipient override.
                Lets the operator confirm deliverability to external
                inboxes (gmail/proton/etc.) without changing the
                configured `admin_notification_email`. Empty input ⇒
                backend falls back to the admin notification address. */}
            <div className="p-4 rounded bg-surface-highlight border border-white/10 space-y-3">
              <div>
                <p className="text-white font-medium">Test Email Configuration</p>
                <p className="text-gray-400 text-sm">Send a test email to verify your SMTP settings work correctly</p>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                <div className="flex-1">
                  <Label className="text-gray-300 text-xs">Send test to (optional)</Label>
                  <Input
                    type="email"
                    value={testEmailRecipient}
                    onChange={(e) => setTestEmailRecipient(e.target.value)}
                    className="bg-white border-slate-200 text-slate-900"
                    placeholder={settings.admin_notification_email || "admin@echosensecapital.com"}
                    data-testid="test-email-recipient-input"
                  />
                  <p className="text-gray-500 text-xs mt-1">
                    Leave blank to send to the Admin Notification Email above. Use any external address (e.g. <code>rick@gmail.com</code>) to verify external deliverability.
                  </p>
                </div>
                <Button
                  onClick={handleTestEmail}
                  disabled={testingEmail || !settings.smtp_host || !settings.smtp_username}
                  variant="outline"
                  className="border-gold/50 text-gold hover:bg-gold/10 shrink-0"
                  data-testid="test-email-button"
                >
                  <Send size={16} className="mr-2" />
                  {testingEmail ? "Sending..." : "Send Test Email"}
                </Button>
              </div>
            </div>

            <div className="p-4 rounded bg-gold/10 border border-gold/30">
              <p className="text-gold text-sm font-medium">Microsoft 365 SMTP Setup (fallback)</p>
              <p className="text-gray-400 text-xs mt-1">
                Outbound email now goes through Resend (above) — these SMTP settings are a fallback only. If used, echosensecapital.com mail is on Microsoft 365 (autodiscover → outlook.com):
              </p>
              <ul className="text-gray-400 text-xs mt-2 space-y-1 list-disc list-inside">
                <li>SMTP Host: <code className="text-gold">smtp.office365.com</code></li>
                <li>SMTP Port: <code className="text-gold">587</code> (STARTTLS)</li>
                <li>Username: Your full email address (admin@echosensecapital.com)</li>
                <li>Password: a Microsoft 365 app password (not your login password) — generate at https://mysignins.microsoft.com/security-info and paste it into the SMTP Password field above; "Authenticated SMTP" must be enabled on the mailbox</li>
              </ul>
              <p className="text-gray-500 text-[11px] mt-2">
                Deliverability also needs SPF (<code className="text-gold">include:spf.protection.outlook.com</code>) + DKIM aligned to M365, or mail is quarantined. Resend (above) avoids these constraints.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Paytiko Payment Rails Settings */}
        <Card className="bg-surface border-white/5 lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-white font-serif flex items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                <CreditCard className="text-burgundy" size={20} />
                Paytiko Payment Rails
              </span>
              {/* Configured-state badge — reads from GET /paytiko/status.
                  Updates after save without a manual refresh. Post
                  forge-20260523-003: keys on Webhook Secret presence only
                  (not on the legacy direct-API triple). The "Webhook"
                  prefix is a deliberate honesty tightening — the badge no
                  longer claims "Live (env)" implying full direct-API
                  readiness; it now says exactly what is configured:
                  the webhook receiver in the named environment. */}
              {(() => {
                let label, dotClass, badgeClass;
                if (!paytikoStatus.configured) {
                  label = "Not configured";
                  dotClass = "bg-gray-400";
                  badgeClass = "bg-gray-500/15 border-gray-500/30 text-gray-300";
                } else if (paytikoStatus.environment === "production") {
                  label = "Webhook ready (production)";
                  dotClass = "bg-emerald-400";
                  badgeClass = "bg-emerald-500/15 border-emerald-500/30 text-emerald-300";
                } else {
                  label = "Webhook ready (sandbox)";
                  dotClass = "bg-amber-400";
                  badgeClass = "bg-amber-500/15 border-amber-500/30 text-amber-300";
                }
                return (
                  <span
                    className={`flex items-center gap-2 px-2 py-1 rounded-full border text-xs font-normal ${badgeClass}`}
                    data-testid="paytiko-configured-badge"
                  >
                    <span className={`w-2 h-2 rounded-full ${dotClass}`}></span>
                    {label}
                  </span>
                );
              })()}
            </CardTitle>
            <CardDescription>Configure Paytiko for wire transfers and fund deposits from qualified clients</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Mediated handoff banner — see tickets
                forge-20260523-001 (UI clarification) and
                forge-20260523-003 (configured-badge decoupling). EchoSense
                no longer calls Paytiko's API directly for money-movement;
                the three direct-API credential fields below the toggle are
                preserved on the model and are not used by any code path
                today, including the configured-badge signal. */}
            <div
              className="p-4 rounded-lg bg-burgundy/10 border border-burgundy/20"
              data-testid="paytiko-handoff-banner"
            >
              <h4 className="text-burgundy font-medium mb-2">Mediated Handoff Flow</h4>
              <p className="text-gray-400 text-sm">
                EchoSense collects funding-request data, generates a PDF, and
                ships it to the partner. The partner handles Paytiko-side
                signature, verification, and wire initiation on the regulated
                payments rail. EchoSense does <strong>not</strong> call the
                Paytiko API directly for create-customer or initiate-transfer.
              </p>
              <p className="text-gray-400 text-sm mt-2">
                The <strong>Webhook Secret</strong> below is the only Paytiko
                credential that still matters &mdash; it gates HMAC
                verification if the partner or Paytiko ever fires a webhook
                back to us, and it is the sole input to the <em>configured</em>
                status badge surfaced by <code className="bg-obsidian px-1 rounded">/api/paytiko/status</code>.
                The legacy direct-API credential fields (API Key, API Secret,
                Merchant ID) are preserved for future revival but are not
                used by any code path today. Expand the toggle below only if
                you need to view or update them.
              </p>
              <p className="text-gray-500 text-xs mt-2">
                Reference: <code className="bg-obsidian px-1 rounded">docs/FINBPM_MONAXA_INTEGRATION_BRIEF.md</code>.
              </p>
            </div>

            {/* Operational notes — restored after Copilot review of PR #20
                flagged the regression vs. the pre-pivot copy. Wording
                updated to reflect the mediated reality. */}
            <div
              className="p-4 rounded-lg bg-burgundy/10 border border-burgundy/20"
              data-testid="paytiko-operational-notes"
            >
              <h4 className="text-burgundy font-medium mb-2">Operational Notes</h4>
              <ul className="text-gray-400 text-sm space-y-1">
                <li>&bull; <strong>Wire Transfers:</strong> Initiated by the partner on behalf of EchoSense after the funding-request PDF is delivered; EchoSense never touches Paytiko's transfer API directly.</li>
                <li>&bull; <strong>Qualification:</strong> Clients qualify when KYC is approved <em>and</em> the partner-confirmed wire transfer is recorded against the account.</li>
                <li>&bull; <strong>Notifications:</strong> Admin receives email when clients become qualified.</li>
                <li>&bull; <strong>Manual Override:</strong> Admins can manually mark users as qualified if needed (e.g. when the partner confirms a wire out-of-band).</li>
              </ul>
            </div>

            {/* Currently active — webhook + environment. These remain
                visible by default because they may still be in play. */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <Globe className="text-burgundy" size={18} />
                <h4 className="text-white font-medium">Currently Active</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-gray-300">Webhook Secret</Label>
                  <Input
                    type={isMaskedSecret(settings.paytiko_webhook_secret) ? "text" : "password"}
                    value={settings.paytiko_webhook_secret || ""}
                    onChange={(e) => onPaytikoSecretChange("paytiko_webhook_secret", e.target.value)}
                    className="bg-white border-slate-200 text-slate-900"
                    placeholder="whsec_..."
                    data-testid="paytiko-webhook-secret"
                  />
                  {isMaskedSecret(settings.paytiko_webhook_secret) && (
                    <p className="text-gray-500 text-xs">
                      Secret on file. Re-type to update; leave masked to keep current value.
                    </p>
                  )}
                  <p className="text-gray-600 text-xs">
                    Webhook URL: <code className="bg-obsidian px-1 rounded">https://api.echosensecapital.com/api/paytiko/webhook</code>
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Environment</Label>
                  <Select
                    value={settings.paytiko_environment || "sandbox"}
                    onValueChange={(v) => setSettings(prev => ({ ...prev, paytiko_environment: v }))}
                  >
                    <SelectTrigger className="bg-surface border-platinum/20 text-platinum" data-testid="paytiko-environment">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-surface border-white/10">
                      <SelectItem value="sandbox">Sandbox (Testing)</SelectItem>
                      <SelectItem value="production">Production (Live)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-gray-600 text-xs">
                    Selects the base URL the legacy direct-API client would
                    use if revived (<code className="bg-obsidian px-1 rounded">sandbox.paytiko.com</code> vs.
                    <code className="bg-obsidian px-1 rounded">api.paytiko.com</code>). Has no
                    effect on the mediated money-movement path that is
                    live today and does not drive the configured-state badge
                    (which keys on Webhook Secret presence only).
                  </p>
                </div>
              </div>
            </div>

            {/* Legacy direct-API credentials toggle.
                Session-local — defaults to hidden on every page load.
                See ticket forge-20260523-001 for rationale. */}
            <div className="pt-2">
              <button
                type="button"
                onClick={() => setShowLegacyPaytikoFields(prev => !prev)}
                className="text-burgundy hover:underline text-sm"
                data-testid="paytiko-legacy-toggle"
                aria-expanded={showLegacyPaytikoFields}
                aria-controls="paytiko-legacy-fields"
              >
                {showLegacyPaytikoFields
                  ? "Hide legacy direct-API fields"
                  : "Show legacy direct-API fields"}
              </button>
            </div>

            {showLegacyPaytikoFields && (
              <div
                id="paytiko-legacy-fields"
                className="space-y-4 p-4 rounded-lg bg-obsidian/40 border border-white/5"
                data-testid="paytiko-legacy-fields"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Key className="text-burgundy" size={18} />
                  <h4 className="text-white font-medium">
                    Legacy direct-API credentials
                    <span className="ml-2 text-gray-500 text-xs font-normal">
                      preserved but unused
                    </span>
                  </h4>
                </div>
                <p className="text-gray-500 text-xs">
                  These fields drive the direct-Paytiko-API code path
                  (<code className="bg-obsidian px-1 rounded">backend/services/paytiko_client.py</code>),
                  which is currently dormant for money-movement. Existing
                  stored values are preserved; you only need to populate them
                  if direct-API integration is restored.
                </p>
                <p className="text-gray-500 text-xs">
                  <strong>All-or-none save rule:</strong> if you edit any of
                  the three fields below, you must also (re-)enter the
                  <strong> Webhook Secret</strong> from the "Currently Active"
                  section above. The backend validator requires all four
                  credential fields (API Key, API Secret, Merchant ID, Webhook
                  Secret) to be set together or cleared together; mixing
                  non-empty and empty across the four is rejected.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-gray-300">API Key</Label>
                      <Input
                        type={isMaskedSecret(settings.paytiko_api_key) ? "text" : "password"}
                        value={settings.paytiko_api_key || ""}
                        onChange={(e) => onPaytikoSecretChange("paytiko_api_key", e.target.value)}
                        className="bg-white border-slate-200 text-slate-900"
                        placeholder="pk_live_..."
                        data-testid="paytiko-api-key"
                      />
                      {isMaskedSecret(settings.paytiko_api_key) && (
                        <p className="text-gray-500 text-xs">
                          Secret on file. Re-type to update; leave masked to keep current value.
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-300">API Secret</Label>
                      <Input
                        type={isMaskedSecret(settings.paytiko_api_secret) ? "text" : "password"}
                        value={settings.paytiko_api_secret || ""}
                        onChange={(e) => onPaytikoSecretChange("paytiko_api_secret", e.target.value)}
                        className="bg-white border-slate-200 text-slate-900"
                        placeholder="sk_live_..."
                        data-testid="paytiko-api-secret"
                      />
                      {isMaskedSecret(settings.paytiko_api_secret) && (
                        <p className="text-gray-500 text-xs">
                          Secret on file. Re-type to update; leave masked to keep current value.
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-gray-300">Merchant ID</Label>
                      <Input
                        type="text"
                        value={settings.paytiko_merchant_id || ""}
                        onChange={(e) => onPaytikoSecretChange("paytiko_merchant_id", e.target.value)}
                        className="bg-white border-slate-200 text-slate-900"
                        placeholder="mer_..."
                        data-testid="paytiko-merchant-id"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Monaxa Partner-Account Settings (MONAXA-REALDATA-008) */}
        <Card className="bg-surface border-white/5 lg:col-span-2" data-testid="monaxa-settings-card">
          <CardHeader>
            <CardTitle className="text-white font-serif flex items-center gap-2">
              <Server className="text-burgundy" size={20} />
              Monaxa Partner Account
            </CardTitle>
            <CardDescription>
              REST API base URL and the Tailscale-fronted egress-proxy shared secret. See <code className="bg-obsidian px-1 rounded">docs/runbooks/monaxa-egress-proxy.md</code> for operator setup.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">API URL</Label>
                  <Input
                    type="text"
                    value={settings.monaxa_api_url || ""}
                    onChange={(e) => setSettings(prev => ({ ...prev, monaxa_api_url: e.target.value }))}
                    className="bg-white border-slate-200 text-slate-900"
                    placeholder="https://account.monaxa.com or https://<proxy-fqdn>"
                    data-testid="monaxa-api-url-input"
                  />
                  <p className="text-gray-600 text-xs">
                    Bare origin only (no <code>/rest</code> suffix — the backend client appends that to every request path itself). Direct: <code>https://account.monaxa.com</code>. Via egress proxy: <code>https://&lt;proxy-fqdn&gt;</code>. Flipping this URL is the operator-controlled cutover lever for the proxy path; pair with a populated <strong>Proxy Secret</strong> below.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">Holder ID</Label>
                  <Input
                    type="text"
                    value={settings.monaxa_holder_id || ""}
                    onChange={(e) => setSettings(prev => ({ ...prev, monaxa_holder_id: e.target.value }))}
                    className="bg-white border-slate-200 text-slate-900"
                    placeholder="188589"
                    data-testid="monaxa-holder-id-input"
                  />
                  <p className="text-gray-600 text-xs">
                    The partner-side profile the member sub-accounts live under (STARSEED-CAPMARKETS-133). A typed account number is checked against <strong>this holder's</strong> account list before it is attached to a member. Leave empty to disable that check — the partner console will then refuse every attach rather than accept an unchecked one.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Environment</Label>
                  <Select
                    value={settings.monaxa_environment || "demo"}
                    onValueChange={(value) => setSettings(prev => ({ ...prev, monaxa_environment: value }))}
                  >
                    <SelectTrigger className="bg-surface border-platinum/20 text-platinum" data-testid="monaxa-environment-select">
                      <SelectValue placeholder="Select environment" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="demo">demo</SelectItem>
                      <SelectItem value="production">production</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-gray-600 text-xs">
                    Informational only — the value is surfaced in logs and in the integration status badge but does not gate the wire path.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Partner Enablement Email</Label>
                  <Input
                    type="email"
                    value={settings.partner_enablement_email || ""}
                    onChange={(e) => setSettings(prev => ({ ...prev, partner_enablement_email: e.target.value }))}
                    className="bg-white border-slate-200 text-slate-900"
                    placeholder="enablement contact at the partner"
                    data-testid="partner-enablement-email-input"
                  />
                  <p className="text-gray-600 text-xs">
                    Where the filled participation agreement is emailed when a member opens a membership (Investor File → Participation Agreement). The send action is blocked until this is set.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Partner Agreement Review Email</Label>
                  <Input
                    type="email"
                    value={settings.partner_agreement_email || ""}
                    onChange={(e) => setSettings(prev => ({ ...prev, partner_agreement_email: e.target.value }))}
                    className="bg-white border-slate-200 text-slate-900"
                    placeholder="management@finbpm.tech"
                    data-testid="partner-agreement-email-input"
                  />
                  <p className="text-gray-600 text-xs">
                    Where investor-signed participation agreements are routed for partner review and countersignature. Admin re-send is blocked until this is set.
                  </p>
                </div>
              </div>

              {/* FinBPM funding instructions (funding-instructions-002/-003):
                  per-asset receivable wallets + SSB wire details + the
                  instructions email Reply-To. Values come from the partner —
                  enter and verify character-for-character; never hardcoded. */}
              <div className="space-y-4 border-t border-white/10 pt-4">
                <p className="text-sm font-semibold text-gray-200">FinBPM funding instructions</p>
                <p className="text-gray-600 text-xs">
                  Used by the funding-instructions email and the member funding page.
                  Wallets are per asset/network — members are warned that wrong-network
                  sends are unrecoverable, so verify each address against the partner&apos;s
                  message before saving.
                </p>
                <div className="space-y-2">
                  <Label className="text-gray-300">USDT wallet — TRC-20 (Tron)</Label>
                  <Input
                    value={settings.finbpm_wallet_usdt_trc20 || ""}
                    onChange={(e) => setSettings(prev => ({ ...prev, finbpm_wallet_usdt_trc20: e.target.value }))}
                    className="bg-white border-slate-200 text-slate-900"
                    placeholder="T..."
                    data-testid="finbpm-wallet-usdt-trc20-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">USDT wallet — ERC-20 (Ethereum)</Label>
                  <Input
                    value={settings.finbpm_wallet_usdt_erc20 || ""}
                    onChange={(e) => setSettings(prev => ({ ...prev, finbpm_wallet_usdt_erc20: e.target.value }))}
                    className="bg-white border-slate-200 text-slate-900"
                    placeholder="0x..."
                    data-testid="finbpm-wallet-usdt-erc20-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">USDC wallet — ERC-20 (Ethereum)</Label>
                  <Input
                    value={settings.finbpm_wallet_usdc_erc20 || ""}
                    onChange={(e) => setSettings(prev => ({ ...prev, finbpm_wallet_usdc_erc20: e.target.value }))}
                    className="bg-white border-slate-200 text-slate-900"
                    placeholder="0x..."
                    data-testid="finbpm-wallet-usdc-erc20-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Wire — Beneficiary</Label>
                  <Input
                    value={settings.finbpm_wire_beneficiary || ""}
                    onChange={(e) => setSettings(prev => ({ ...prev, finbpm_wire_beneficiary: e.target.value }))}
                    className="bg-white border-slate-200 text-slate-900"
                    placeholder="FINBPM LIMITED"
                    data-testid="finbpm-wire-beneficiary-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Wire — Bank name</Label>
                  <Input
                    value={settings.finbpm_wire_bank_name || ""}
                    onChange={(e) => setSettings(prev => ({ ...prev, finbpm_wire_bank_name: e.target.value }))}
                    className="bg-white border-slate-200 text-slate-900"
                    placeholder="SSB Bank"
                    data-testid="finbpm-wire-bank-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Wire — Bank address</Label>
                  <Input
                    value={settings.finbpm_wire_bank_address || ""}
                    onChange={(e) => setSettings(prev => ({ ...prev, finbpm_wire_bank_address: e.target.value }))}
                    className="bg-white border-slate-200 text-slate-900"
                    placeholder="street, city, state, country"
                    data-testid="finbpm-wire-bank-address-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Wire — Account number</Label>
                  <Input
                    value={settings.finbpm_wire_account_number || ""}
                    onChange={(e) => setSettings(prev => ({ ...prev, finbpm_wire_account_number: e.target.value }))}
                    className="bg-white border-slate-200 text-slate-900"
                    placeholder=""
                    data-testid="finbpm-wire-account-number-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Wire — SWIFT / BIC</Label>
                  <Input
                    value={settings.finbpm_wire_swift || ""}
                    onChange={(e) => setSettings(prev => ({ ...prev, finbpm_wire_swift: e.target.value }))}
                    className="bg-white border-slate-200 text-slate-900"
                    placeholder=""
                    data-testid="finbpm-wire-swift-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Wire — Routing (ABA/Fedwire)</Label>
                  <Input
                    value={settings.finbpm_wire_routing || ""}
                    onChange={(e) => setSettings(prev => ({ ...prev, finbpm_wire_routing: e.target.value }))}
                    className="bg-white border-slate-200 text-slate-900"
                    placeholder=""
                    data-testid="finbpm-wire-routing-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Instructions email Reply-To</Label>
                  <Input
                    value={settings.finbpm_instructions_reply_to || ""}
                    onChange={(e) => setSettings(prev => ({ ...prev, finbpm_instructions_reply_to: e.target.value }))}
                    className="bg-white border-slate-200 text-slate-900"
                    placeholder="management@finbpm.tech"
                    data-testid="finbpm-instructions-reply-to-input"
                  />
                </div>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">API Key</Label>
                  <Input
                    type={isMaskedSecret(settings.monaxa_api_key) ? "text" : "password"}
                    value={settings.monaxa_api_key || ""}
                    onChange={(e) => onMonaxaSecretChange("monaxa_api_key", e.target.value)}
                    className="bg-white border-slate-200 text-slate-900"
                    placeholder="Bearer token from Monaxa"
                    data-testid="monaxa-api-key-input"
                  />
                  {isMaskedSecret(settings.monaxa_api_key) && (
                    <p className="text-gray-500 text-xs">
                      Key on file. Re-type to update; leave masked to keep current value.
                    </p>
                  )}
                  <p className="text-gray-600 text-xs">
                    Bearer token sent in the <code>Authorization</code> header to authenticate every Monaxa REST request (independent of the proxy hop). Issued by Monaxa per tenant. Empty value means <code>MonaxaHttpClient.from_settings()</code> raises <code>MonaxaNotConfigured</code> and the hourly sweep aborts each tick — set this before flipping <code>MONAXA_ENABLED</code> on.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Client-Area Token (/ca-rest)</Label>
                  <Input
                    type={isMaskedSecret(settings.monaxa_ca_rest_token) ? "text" : "password"}
                    value={settings.monaxa_ca_rest_token || ""}
                    onChange={(e) => onMonaxaSecretChange("monaxa_ca_rest_token", e.target.value)}
                    className="bg-white border-slate-200 text-slate-900"
                    placeholder="Token from account.monaxa.com/ca-rest/login"
                    data-testid="monaxa-ca-rest-token-input"
                  />
                  {isMaskedSecret(settings.monaxa_ca_rest_token) && (
                    <p className="text-gray-500 text-xs">
                      Token on file. Re-type to update; leave masked to keep current value.
                    </p>
                  )}
                  <p className="text-gray-600 text-xs">
                    Optional. A <strong>second</strong> credential for the partner&apos;s client-area
                    surface (<code>/ca-rest/*</code>), which is distinct from the <code>/rest/*</code>
                    broker surface the API Key above authenticates. Used only by the
                    CAPMARKETS-227 credential diff, which reports which paths one credential
                    reaches that the other does not. Leaving it empty is safe — the diff reports
                    it as unconfigured rather than treating every path as unreachable.
                  </p>
                  <p className="text-amber-600 text-xs">
                    The credential supplied 2026-08-30 is a <strong>personal login</strong>
                    (partner user <code>finbpm</code>), so calls made with it are attributable to a
                    named individual at the partner rather than to this service. Appropriate for a
                    bounded read-only diagnostic; ask for a service credential before relying on it
                    for anything ongoing.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Statement Reading Key (Anthropic)</Label>
                  <Input
                    type={isMaskedSecret(settings.anthropic_api_key) ? "text" : "password"}
                    value={settings.anthropic_api_key || ""}
                    onChange={(e) => onMonaxaSecretChange("anthropic_api_key", e.target.value)}
                    className="bg-white border-slate-200 text-slate-900"
                    placeholder="sk-ant-..."
                    data-testid="anthropic-api-key-input"
                  />
                  {isMaskedSecret(settings.anthropic_api_key) && (
                    <p className="text-gray-500 text-xs">
                      Key on file. Re-type to update; leave masked to keep current value.
                    </p>
                  )}
                  <p className="text-gray-600 text-xs">
                    Optional (STARSEED-CAPMARKETS-230). Lets{" "}
                    <strong>Enter partner balances</strong> read the figures out of a
                    statement screenshot instead of an operator typing them into cells.
                    Nothing is written from what it reads: each figure is ranked against
                    that account&apos;s own history and shown for confirmation first.
                    Leaving it empty is safe — the screen falls back to manual entry.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Egress Proxy Secret</Label>
                  <Input
                    type={isMaskedSecret(settings.monaxa_proxy_secret) ? "text" : "password"}
                    value={settings.monaxa_proxy_secret || ""}
                    onChange={(e) => onMonaxaSecretChange("monaxa_proxy_secret", e.target.value)}
                    className="bg-white border-slate-200 text-slate-900"
                    placeholder="openssl rand -hex 32"
                    data-testid="monaxa-proxy-secret-input"
                  />
                  {isMaskedSecret(settings.monaxa_proxy_secret) && (
                    <p className="text-gray-500 text-xs">
                      Secret on file. Re-type to update; leave masked to keep current value.
                    </p>
                  )}
                  <p className="text-gray-600 text-xs">
                    Shared-header value (<code>X-EqhoSense-Proxy-Secret</code>) the backend sends on every Monaxa request when the API URL points at the egress proxy. Empty value means the header is NOT injected — leave empty for the direct-to-Monaxa path. Generate with <code>openssl rand -hex 32</code>. The proxy node's Caddyfile must carry the SAME value.
                  </p>
                </div>
                <div className="p-3 rounded bg-burgundy/10 border border-burgundy/30 text-xs text-gray-300">
                  <strong className="text-burgundy block mb-1">Operator setup required before flipping the URL</strong>
                  The egress proxy must be provisioned (Tailscale + Caddy on a tailnet node) and Monaxa must allowlist the proxy node's public IP before the proxy URL can replace <code>https://account.monaxa.com</code>. Follow the 13-step runbook in <code>docs/runbooks/monaxa-egress-proxy.md</code>.
                </div>
              </div>
            </div>

            {/* Test Connection — pings Monaxa through the full configured
                chain via GET /monaxa/health (forge-20260530-005). */}
            <div className="flex flex-wrap items-center gap-4 pt-4 border-t border-white/5" data-testid="monaxa-test-connection-row">
              <Button
                onClick={handleTestMonaxa}
                disabled={testingMonaxa}
                variant="outline"
                className="border-burgundy/50 text-burgundy hover:bg-burgundy/10"
                data-testid="monaxa-test-connection-button"
              >
                <Server size={16} className="mr-2" />
                {testingMonaxa ? "Testing…" : "Test Connection"}
              </Button>
              {monaxaHealth && (
                <div
                  className={`flex items-center gap-2 text-sm ${monaxaHealth.ok ? "text-emerald" : "text-red-400"}`}
                  data-testid="monaxa-health-result"
                >
                  <span className={`inline-block w-2 h-2 rounded-full ${monaxaHealth.ok ? "bg-emerald" : "bg-red-500"}`}></span>
                  <span>
                    {monaxaHealth.message}
                    {/* Show timing whenever the endpoint returned it — on
                        failures too (slow-timeout diagnostics from the
                        browser). Absent only on a request-level error where
                        our own backend never responded. */}
                    {monaxaHealth.elapsed_ms != null ? ` (${monaxaHealth.elapsed_ms}ms)` : ""}
                  </span>
                </div>
              )}
              <p className="text-gray-600 text-xs w-full">
                Runs <code>GET /rest/ping</code> through the full configured chain (including the egress proxy hop). Green confirms the backend reaches Monaxa end-to-end. A red <em>"reached Monaxa but key rejected"</em> means the chain works and only the API Key needs fixing.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Engagement Tools Settings */}
        <Card className="bg-surface border-white/5 lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-white font-serif flex items-center gap-2">
              <MessageCircle className="text-burgundy" size={20} />
              Engagement Tools
            </CardTitle>
            <CardDescription>Configure Calendly scheduling and Tawk.to live chat for prospect engagement</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Calendly Settings */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="text-burgundy" size={18} />
                  <h4 className="text-white font-medium">Calendly Scheduling</h4>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Calendly Event URL</Label>
                  <Input
                    type="text"
                    value={settings.calendly_url || ""}
                    onChange={(e) => setSettings(prev => ({ ...prev, calendly_url: e.target.value }))}
                    className="bg-white border-slate-200 text-slate-900"
                    placeholder="https://calendly.com/your-name/30min"
                    data-testid="calendly-url-input"
                  />
                  <p className="text-gray-600 text-xs">
                    Enter your Calendly event link. Get one free at{" "}
                    <a href="https://calendly.com" target="_blank" rel="noopener noreferrer" className="text-burgundy hover:underline">
                      calendly.com
                    </a>
                  </p>
                </div>
                <div className="space-y-2 mt-4">
                  <Label className="text-gray-300">Intro Video URL</Label>
                  <Input
                    type="text"
                    value={settings.intro_video_url || ""}
                    onChange={(e) => setSettings(prev => ({ ...prev, intro_video_url: e.target.value }))}
                    className="bg-white border-slate-200 text-slate-900"
                    placeholder="https://example.com/intro-video.mp4"
                    data-testid="intro-video-url-input"
                  />
                  <p className="text-gray-600 text-xs">
                    Optional: Upload intro video to cloud storage (e.g., AWS S3, Google Drive) and paste the direct link here
                  </p>
                </div>
              </div>

              {/* Tawk.to Settings */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <MessageCircle className="text-burgundy" size={18} />
                  <h4 className="text-white font-medium">Tawk.to Live Chat</h4>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Tawk.to Property ID</Label>
                  <Input
                    type="text"
                    value={settings.tawk_property_id || ""}
                    onChange={(e) => setSettings(prev => ({ ...prev, tawk_property_id: e.target.value }))}
                    className="bg-white border-slate-200 text-slate-900"
                    placeholder="1234567890abcdef12345678"
                    data-testid="tawk-id-input"
                  />
                  <p className="text-gray-600 text-xs">
                    Get your free Property ID from{" "}
                    <a href="https://www.tawk.to" target="_blank" rel="noopener noreferrer" className="text-burgundy hover:underline">
                      tawk.to
                    </a>
                    {" "}→ Admin → Property Settings → Widget Code
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4 rounded bg-burgundy/10 border border-burgundy/30">
              <p className="text-burgundy text-sm font-medium">Engagement Setup Guide</p>
              <ul className="text-gray-400 text-xs mt-2 space-y-1 list-disc list-inside">
                <li><strong>Calendly:</strong> Sign up free, create a 30-min event, copy the event URL</li>
                <li><strong>Tawk.to:</strong> Sign up free, create a property, find Property ID in widget code (format: embed.tawk.to/PROPERTY_ID/default)</li>
                <li>Both services are 100% free and widely used for professional prospect engagement</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Save Button */}
      <div className="mt-6 flex justify-end">
        <Button 
          onClick={handleSave}
          disabled={saving}
          className="bg-gold hover:bg-gold/90 text-navy-dark px-8"
          data-testid="save-settings"
        >
          <Save size={16} className="mr-2" />
          {saving ? "Saving..." : "Save Settings"}
        </Button>
      </div>
    </DashboardLayout>
  );
};

export default SettingsPage;
